import { Divider, Box } from '@mui/material';
import {
  RestoreAzureDiskConfig,
  RestoreEbsVolumeConfig,
  RestoreGceDiskConfig,
} from '@repo/api-gw-sdk';
import type { RestoreJob } from '@repo/api-gw-sdk';
import { useEffect } from 'react';

import { Loader } from '@/components/layout/loading';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useFields } from '@/components/table/hooks';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';

import { FieldsSection } from './fieldsSection';
import { JobItemFields } from './jobItemFields';
import { JobStatusCard } from './jobStatusCard';
import { OperationSummary } from './operationSummary';
import {
  restoreJobConfigurationFields,
  restoreJobItemFields,
  restoreJobSummaryFromFields,
  restoreJobSummaryToFields,
} from './restoreJobItemFields';
import { StepsSummary } from './stepsSummary';
import { VolumeConfigurationSection } from './volumesConfigurationSection';

interface RestoreJobsCardProps {
  job: RestoreJob | string;
  onClose: () => void;
}

export const RestoreJobsCard = ({ job, onClose }: RestoreJobsCardProps) => {
  return (
    <PanelWrapper
      header={{
        title: 'Job details',
        onClose,
        hideBorder: true,
      }}
      testIdPrefix='restore-jobs-card'
    >
      {typeof job === 'string' ? (
        <JobContentFromId jobId={job} onClose={onClose} />
      ) : (
        <RestoreJobsCardContent job={job} />
      )}
    </PanelWrapper>
  );
};

const JobContentFromId = ({
  jobId,
  onClose,
}: {
  jobId: string;
  onClose: () => void;
}) => {
  const { snackbar } = useWorkspace();
  const dal = useDAL();
  const { data: job, isLoading } = dal.jobs.restore.useJob(jobId);

  useEffect(() => {
    if (!isLoading && !job?.job) {
      onClose();
      snackbar.showWarningMessage(
        'Looks like your link is broken and this job ID is invalid.'
      );
    }
  }, [job, isLoading, onClose, snackbar]);

  return isLoading || !job?.job ? (
    <Loader />
  ) : (
    <RestoreJobsCardContent job={job.job} />
  );
};

const RestoreJobsCardContent = ({ job }: { job: RestoreJob }) => {
  const fromCloudProvider =
    job.vault?.vaultAttributes.cloudProvider ||
    job.resourceDetails?.cloudProvider;
  const toCloudProvider = job.destinationDetails.cloudProvider;
  const configurationFields = useFields(restoreJobConfigurationFields, job);

  const isVolumeConfig = VolumeConfigs.some(
    (config) => job.configuration instanceof config
  );

  return (
    <Box className='pb-[40px]' overflow='auto'>
      <JobItemFields job={job} fields={restoreJobItemFields} />
      <Divider />
      <JobStatusCard job={job} />
      <OperationSummary
        job={job}
        fromTitle='Restore from'
        fromFields={restoreJobSummaryFromFields}
        fromCloudProvider={fromCloudProvider}
        toTitle='Restore to'
        toFields={restoreJobSummaryToFields}
        toCloudProvider={toCloudProvider}
      />
      {job.configuration && !isVolumeConfig && (
        <FieldsSection
          fields={configurationFields}
          title='Instance Configuration'
          defaultExpanded={true}
        />
      )}
      <VolumeConfigurationSection job={job} cloudProvider={toCloudProvider} />
      <StepsSummary job={job} />
    </Box>
  );
};

const VolumeConfigs = [
  RestoreEbsVolumeConfig,
  RestoreAzureDiskConfig,
  RestoreGceDiskConfig,
];
